import React, { useRef, useState, useCallback } from "react";
import { Camera, Upload, X } from "lucide-react";

// Premium upload / camera / drag-drop zone with polished preview.
export default function UploadZone({ onFile, preview, onClear, label = "Snap your item", testid = "upload-zone" }) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const busyRef = useRef(false);

  const handleFiles = useCallback((files, inputEl) => {
    const f = files?.[0];
    if (inputEl) inputEl.value = "";                 // allow re-picking the same file after a retake
    if (!f) return;
    // Accept any image, plus empty-type camera captures (mobile cameras often report "" or image/heic).
    if (f.type && !f.type.startsWith("image/")) return;
    if (busyRef.current) return;                     // one capture => exactly one analysis request
    busyRef.current = true;
    Promise.resolve(onFile(f)).finally(() => { busyRef.current = false; });
  }, [onFile]);

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    handleFiles(e.dataTransfer.files);
  };

  if (preview) {
    return (
      <div className="relative w-full max-w-md mx-auto" data-testid={`${testid}-preview`}>
        <div className="overflow-hidden border border-[#C8A97E]/40 bg-[#1A1A1A]">
          <img src={preview} alt="Your item preview" className="w-full h-[380px] object-cover" />
        </div>
        <button onClick={onClear} data-testid={`${testid}-clear`}
          className="absolute top-3 right-3 h-9 w-9 flex items-center justify-center bg-[#0D0D0D]/80 border border-[#F5F2EA]/20 hover:border-[#C8A97E] transition-colors">
          <X size={16} className="text-[#F5F2EA]" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto" data-testid={testid}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border border-dashed transition-colors duration-300 px-8 py-16 text-center
          ${drag ? "border-[#C8A97E] bg-[#C8A97E]/5" : "border-[#F5F2EA]/20 hover:border-[#C8A97E]/60"}`}
      >
        <div className="flex justify-center mb-6">
          <div className="h-16 w-16 rounded-full border border-[#C8A97E]/40 flex items-center justify-center">
            <Camera size={26} className="text-[#C8A97E]" strokeWidth={1.25} />
          </div>
        </div>
        <p className="font-serif text-2xl text-[#F5F2EA]">{label}</p>
        <p className="font-sans text-xs tracking-wider text-[#8A8170] mt-3 uppercase">
          Drag &amp; drop · or choose below
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <button onClick={() => cameraRef.current?.click()} data-testid={`${testid}-camera`}
          className="flex items-center justify-center gap-2 border border-[#F5F2EA]/15 py-3 hover:border-[#C8A97E] transition-colors text-sm tracking-wide">
          <Camera size={16} strokeWidth={1.5} /> Take Photo
        </button>
        <button onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload`}
          className="flex items-center justify-center gap-2 border border-[#F5F2EA]/15 py-3 hover:border-[#C8A97E] transition-colors text-sm tracking-wide">
          <Upload size={16} strokeWidth={1.5} /> Upload
        </button>
      </div>

      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => handleFiles(e.target.files, e.target)} data-testid={`${testid}-input`} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => handleFiles(e.target.files, e.target)} data-testid={`${testid}-camera-input`} />
    </div>
  );
}
